import type React from 'react';
import { useState } from 'react';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import userPersonas from '@common/constants/userPersonas';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { isDueDateValid } from './validation';
import dueDates from './dueDates.json';
import {
  Alert,
  Box,
  Button,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const DUE_DATE_HELP_TEXT = 'We’ll ask your renter to send their rent on or before this day';

interface Props {
  paymentDayOfTheMonth: number;
  editMode: boolean;
  onSubmit: (requestPayload: Record<string, unknown>) => any;
  onClose: () => void;
  onCancel: () => void;
  onBack: (requestPayload: Record<string, unknown>) => void;
  trackValidationError: (data: { errors: Array<Record<string, any>> }) => void;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

const DueDatePage: React.FC<Props> = ({
  paymentDayOfTheMonth: initialPaymentDayOfTheMonth = 1,
  editMode = false,
  onSubmit,
  onClose,
  onCancel,
  onBack,
  trackValidationError,
  currentProperty,
  user,
}) => {
  const [paymentDayOfTheMonth, setPaymentDayOfTheMonth] = useState(initialPaymentDayOfTheMonth);
  const [showCaveat, setShowCaveat] = useState(false);
  const [error, setError] = useState('');
  const [editPaymentDayOfTheMonth, setEditPaymentDayOfTheMonth] = useState(false);

  const onChangeSelect = (e): void => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const newPaymentDayOfTheMonth = parseInt(selectedOption.getAttribute('value'), 10);
    const newShowCaveat = selectedOption.innerText?.endsWith('*');

    setShowCaveat(newShowCaveat);
    setPaymentDayOfTheMonth(newPaymentDayOfTheMonth);
    setError('');
    setEditPaymentDayOfTheMonth(true);
  };

  const handleSubmit = (): Promise<any> => {
    return Promise.resolve().then(() => {
      const validationError = isDueDateValid(paymentDayOfTheMonth);

      if (validationError) {
        setError(validationError);
        trackValidationError({ errors: [{ dueDateError: validationError }] });
        return { error: true };
      }

      IreneOnboardingCSEvents.emitContinueFromDueDatePageEvent({ currentProperty, user });

      const payload = editMode
        ? {
            editDayOfTheMonth: paymentDayOfTheMonth,
          }
        : {
            paymentDayOfTheMonth,
          };

      return onSubmit(payload);
    });
  };

  const handleCancel = (): void => {
    onCancel();
  };

  const handleClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onClose();
  };

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onBack({
      paymentDayOfTheMonth,
      editPaymentDayOfTheMonth,
    });
  };

  const renderStartDateOptions = () => {
    const defaultOption = (
      <option key={-1} disabled value="">
        Select one
      </option>
    );
    const options = dueDates.map((date, index) => {
      if (date === null) {
        return (
          <option key={index} disabled>
            ──────
          </option>
        );
      }

      const [text, value] = date;

      return (
        <option key={index} value={value}>
          {text}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  const startDateOptions = renderStartDateOptions();

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent faqs={FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE_FAST_PAYMENTS} />}
      onRequestClose={handleClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Heading level={3} textStyle="body-lg-bold">
            When should your tenant send their rent each month?
          </Heading>
          <FormField
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 36rem` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            css={{ maxWidth: '36rem' }}
            error={Boolean(error)}
            required
            control={
              <Select size="md" value={paymentDayOfTheMonth} onChange={onChangeSelect}>
                {startDateOptions}
              </Select>
            }
            label={<Label>Monthly payment date</Label>}
            description={<FormHelp>{error || DUE_DATE_HELP_TEXT}</FormHelp>}
          />

          {showCaveat && (
            <Paragraph textStyle="body-sm">
              For any month with fewer than {paymentDayOfTheMonth} days, the last day of the month will be the default
              payment date.
            </Paragraph>
          )}

          <Alert
            tone="info"
            actionButton={
              <TextButton
                onClick={() =>
                  window.open('https://www.zillow.help/article/What-are-Fast-Payments', '_blank', 'noopener,noreferrer')
                }
              >
                Learn more about payment timing
              </TextButton>
            }
          >
            Payments are expected to be deposited within two business days. In some cases, such as when a renter is
            within their first week of setting up and starting payments on Zillow, payments follow a standard
            five-business-day timeline.
          </Alert>
        </Box>
      }
      backAction={{ onClick: gotoPreviousPage }}
      footerAriaLabel="Payment due date actions"
      footerEnd={
        <>
          {editMode && <TextButton onClick={handleCancel}>Cancel</TextButton>}
          <Button emphasis="filled" onClick={handleSubmit}>
            {editMode ? 'Update' : 'Continue'}
          </Button>
        </>
      }
    />
  );
};

export default DueDatePage;
